<script>
  import { onMount, tick } from 'svelte';
  import { api } from '../lib/api.js';
  import { CHAT_LIST_FILTERS, buildNewChatActions, filterChatsByPreset, filterChatsByQuery, formatChatListDate, getChatPreviewLabel, isGeneratingState, splitPinnedChats, templateIdFromAction } from '../lib/chat-state.js';
  import { appSettingsStore, chatActivityStore, chatNotificationsResultText, enableChatNotifications, ensureTemplatesLoaded, loadTemplates, refreshChatsInBackground, takeFreshStartChats, templatesStore, toggleChatNotifications, toggleChatPin, userStore, usersStore } from '../lib/stores.js';
  import { TEXT } from '../lib/ui-text.js';
  import { getDisplayNameFallback } from '../lib/app-settings.js';
  import { getSessionChatSearch, setSessionChatSearch } from '../lib/storage.js';
  import { subscribeSyncEvents } from '../lib/sync.js';
  import { longpress } from '../lib/actions/longpress.js';
  import { rowSwipe } from '../lib/actions/row-swipe.js';
  import { primeKeyboard, releaseKeyboard } from '../lib/keyboard-handoff.js';
  import { readChatListView, saveChatListView } from '../lib/chat-list-view.js';
  import { pageEntrance, setEntrance } from '../lib/page-transition.js';
  import BrainStatusLine from '../lib/components/BrainStatusLine.svelte';
  import ActionSheet from '../lib/components/ActionSheet.svelte';
  import ChatMembersSheet from '../lib/components/ChatMembersSheet.svelte';
  import { 
    archivedCountStore,
    chatsStore, 
    showArchivedStore, 
    loadChats, 
    createChat, 
    deleteChat,
    toggleArchive,
    renameChat,
    loadingStore,
    chatsLoadedStore,
    chatsLoadErrorStore,
    classifyChatError,
    setCurrentChat,
    showFlash,
    pushRestoreStore,
    restorePushFromBanner,
    dismissPushBanner,
    pushRestoreResultText,
  } from '../lib/stores.js';

  let editingChatId = null;
  let editTitle = '';
  let menuChat = null; // чат, для которого открыта шторка действий (⋮)
  let membersSheetChat = null; // чат, для которого открыта шторка «Поделиться/Участники»
  let newChatSheetOpen = false; // long-press по «+ Новый» → выбор типа чата
  let actionError = '';
  let editInput = null;
  let searchQuery = '';
  let debouncedSearchQuery = '';
  let searchResults = [];
  let searchLoading = false;
  let searchNextCursor = null;
  let searchHasMore = false;
  let searchDebounceTimer = null;
  // ТЗ §6.2.4: поиск по сообщениям — не раньше 500 мс после последнего символа.
  const SEARCH_DEBOUNCE_MS = 500;
  let searchSeq = 0;
  // В4 (1.27.3): фильтр и прокрутка — те же, что были до перехода в чат.
  let activeFilter = readChatListView().filter;
  let listContainer = null;
  let syncCleanup = null;
  // Одна строка навбара: [аватар][поиск с фильтр-иконкой][написать]. Фильтры и
  // архив — в шторке (паттерн кнопки фильтра у строки поиска в Почте iOS).
  // Активный фильтр обязан быть виден: иконка подсвечена, плейсхолдер
  // «Поиск · Недавние» — иначе «пропавшие чаты» через день.
  let filterSheetOpen = false;
  $: listFilter = $showArchivedStore ? 'archive' : activeFilter;
  $: filterIsActive = listFilter !== 'all';
  $: searchPlaceholder = filterIsActive ? `Поиск · ${getFilterLabel(listFilter)}` : 'Поиск';
  $: filterSheetActions = [
    { id: 'all', label: getFilterLabel('all'), checked: listFilter === 'all' },
    { id: 'recent', label: getFilterLabel('recent'), checked: listFilter === 'recent' },
    { id: 'with-images', label: getFilterLabel('with-images'), checked: listFilter === 'with-images' },
    { id: 'travel', label: getFilterLabel('travel'), checked: listFilter === 'travel' },
    { id: 'scan', label: getFilterLabel('scan'), checked: listFilter === 'scan' },
    { id: 'list', label: getFilterLabel('list'), checked: listFilter === 'list' },
    {
      id: 'archive',
      label: $archivedCountStore > 0 ? `${getFilterLabel('archive')} · ${$archivedCountStore}` : getFilterLabel('archive'),
      checked: listFilter === 'archive',
    },
  ];

  async function applyListFilter(id) {
    if (id === 'archive') {
      activeFilter = 'all';
      await switchArchiveTab(true);
      return;
    }
    activeFilter = id;
    if ($showArchivedStore) {
      await switchArchiveTab(false);
    }
  }

  async function handleFilterSelect(event) {
    filterSheetOpen = false;
    await applyListFilter(event.detail.id);
  }

  $: normalizedSearchQuery = searchQuery.trim();
  // Поиск двухуровневый: локальная фильтрация видимого списка по заголовку/превью —
  // мгновенно с 1-го символа; серверный поиск по СОДЕРЖИМОМУ сообщений — от 3 символов
  // с дебаунсом, отдельной секцией «Совпадения в сообщениях» ниже.
  $: hasQuery = normalizedSearchQuery.length > 0;
  $: isSearchMode = debouncedSearchQuery.length >= 3;
  $: visibleChats = filterChatsByPreset($chatsStore, activeFilter, $chatActivityStore);
  $: localMatches = hasQuery ? filterChatsByQuery(visibleChats, normalizedSearchQuery) : [];
  $: displayedChats = hasQuery ? localMatches : visibleChats;
  // Лента рисуется группами (этап 11). Секция «Закреплённые» — только в обычном
  // списке: при поиске уже есть свои заголовки, в архиве закреплённых не бывает
  // (архивация снимает закрепление). Пустые группы отбрасываются.
  $: chatGroups = (hasQuery || $showArchivedStore)
    ? [{ id: 'all', title: '', aria: '', items: displayedChats }]
    : (({ pinned, rest }) => [
        { id: 'pinned', title: TEXT.chats.pinnedSection, aria: TEXT.chats.pinnedListAria, items: pinned },
        { id: 'rest', title: '', aria: pinned.length ? TEXT.chats.otherListAria : '', items: rest },
      ].filter((group) => group.items.length > 0))(splitPinnedChats(displayedChats));
  $: searchSummary = activeFilter === 'all' ? '' : getFilterLabel(activeFilter);

  function scheduleSearch() {
    if (searchDebounceTimer) {
      window.clearTimeout(searchDebounceTimer);
      searchDebounceTimer = null;
    }

    setSessionChatSearch(normalizedSearchQuery);

    if (normalizedSearchQuery.length < 3) {
      debouncedSearchQuery = '';
      searchResults = [];
      searchNextCursor = null;
      searchHasMore = false;
      return;
    }

    searchDebounceTimer = window.setTimeout(() => {
      debouncedSearchQuery = normalizedSearchQuery;
      runSearch(normalizedSearchQuery);
      searchDebounceTimer = null;
    }, SEARCH_DEBOUNCE_MS);
  }

  async function runSearch(query, { append = false, cursor = null } = {}) {
    const currentSeq = ++searchSeq;
    searchLoading = true;
    actionError = '';
    try {
      const result = await api.searchHistory(query, {
        archived: $showArchivedStore ? '1' : 'all',
        cursor,
      });
      if (currentSeq !== searchSeq) {
        return;
      }
      searchResults = append ? [...searchResults, ...(result.items || [])] : (result.items || []);
      searchNextCursor = result.nextCursor || null;
      searchHasMore = Boolean(result.hasMore);
    } catch (error) {
      if (currentSeq !== searchSeq) {
        return;
      }
      if (!append) {
        searchResults = [];
        searchNextCursor = null;
        searchHasMore = false;
      }
      actionError = classifyChatError(error, 'Не удалось выполнить поиск.');
    } finally {
      if (currentSeq === searchSeq) {
        searchLoading = false;
      }
    }
  }

  async function loadMoreSearchResults() {
    if (!isSearchMode || !searchHasMore || searchLoading || !searchNextCursor) {
      return;
    }
    await runSearch(debouncedSearchQuery, { append: true, cursor: searchNextCursor });
  }

  async function refreshSearchIndex({ silent = false } = {}) {
    if (isSearchMode) {
      await runSearch(debouncedSearchQuery);
      return;
    }
    await loadChats($showArchivedStore, { silent });
  }

  // В3 (1.27.3): один чат на серию тапов; переход — сразу после ответа сервера
  // (строку кладёт createChat, список обновляется фоном).
  let creatingChat = false;

  async function handleNewChat(chatType = null, templateId = null) {
    if (creatingChat) {
      return;
    }
    creatingChat = true;
    actionError = '';
    // Клавиатура — в самом тапе и только для текстовых чатов: скан начинают с камеры,
    // список — с панели пунктов.
    if (!chatType || chatType === 'default') {
      primeKeyboard();
    }
    try {
      const chatId = await createChat(null, chatType, templateId);
      if (templateId) {
        showFlash(TEXT.templates.created, 'info');
      }
      window.location.hash = `/chat/${chatId}`;
    } catch (error) {
      releaseKeyboard();
      if (templateId && error?.status === 404) {
        // Шаблон удалили с другого устройства — список устарел.
        showFlash(TEXT.templates.missing, 'info');
        loadTemplates();
        return;
      }
      actionError = classifyChatError(error, 'Не удалось создать чат.');
    } finally {
      creatingChat = false;
    }
  }

  async function handleDelete(chatId) {
    // Подтверждение — inline в шторке действий (ActionSheet confirm), не window.confirm.
    actionError = '';
    try {
      await deleteChat(chatId);
      await refreshSearchIndex();
      showFlash('Чат удалён.', 'info');
    } catch (error) {
      actionError = classifyChatError(error, 'Не удалось удалить чат.');
    }
  }

  async function handleArchive(chatId, isArchived) {
    actionError = '';
    try {
      await toggleArchive(chatId, !isArchived);
      await refreshSearchIndex({ silent: true });
      if (!isArchived) {
        // В5 (1.27.3): архив обратим — тост «Отменить» вместо подтверждения.
        showFlash(TEXT.chats.archived, 'info', 5000, {
          label: TEXT.chats.undo,
          run: () => handleArchive(chatId, true),
        });
      }
    } catch (error) {
      // Тост, а не строка вверху списка: свайп мог быть далеко внизу (1.27.3).
      showFlash(classifyChatError(error, 'Не удалось изменить статус архива.'), 'error');
    }
  }

  // ── Жесты строки (В5): влево — архив, вправо — закрепление ──
  // Строка едет за пальцем над подписью действия (actions/row-swipe.js, 1.27.4). Меню ⋮
  // остаётся для тех, кто жестами не пользуется; удержание строки открывает то же меню.
  async function handleRowSwipe(chat, direction) {
    if (direction === 'left') {
      await handleArchive(chat.chatId, chat.isArchived);
    } else if (direction === 'right' && !chat.isArchived) {
      await handleChatPin(chat);
    }
  }

  async function startRename(chat) {
    editingChatId = chat.chatId;
    editTitle = chat.title || '';
    await tick();
    editInput?.focus();
  }

  async function saveRename() {
    if (editTitle.trim() && editingChatId) {
      try {
        actionError = '';
        await renameChat(editingChatId, editTitle.trim());
        await refreshSearchIndex();
      } catch (error) {
        actionError = classifyChatError(error, 'Не удалось переименовать чат.');
      }
    }
    editingChatId = null;
    editTitle = '';
  }

  function cancelRename() {
    editingChatId = null;
    editTitle = '';
  }

  function handleKeydown(e) {
    if (e.key === 'Enter') {
      saveRename();
    } else if (e.key === 'Escape') {
      cancelRename();
    }
  }

  function openChatMenu(chat, e) {
    e.stopPropagation();
    e.preventDefault();
    menuChat = chat;
  }

  // Владелец: rename/share/archive/delete; участник общего чата: состав, архив, выход.
  $: chatMenuActions = menuChat
    ? (menuChat.isOwner !== false
        ? [
            ...(menuChat.isArchived ? [] : [{ id: 'pin', label: menuChat.isPinned ? TEXT.chats.unpinAction : TEXT.chats.pinAction, icon: 'pin' }]),
            { id: 'rename', label: 'Переименовать', icon: 'rename' },
            { id: 'share', label: TEXT.family.shareTitle },
            { id: 'notifications', label: menuChat.notificationsEnabled ? TEXT.family.disableNotifications : TEXT.family.enableNotifications },
            { id: 'archive', label: menuChat.isArchived ? 'Вернуть из архива' : 'В архив', icon: 'archive' },
            { id: 'delete', label: 'Удалить', icon: 'trash', danger: true, confirm: 'Удалить чат? Восстановить не получится.' },
          ]
        : [
            ...(menuChat.isArchived ? [] : [{ id: 'pin', label: menuChat.isPinned ? TEXT.chats.unpinAction : TEXT.chats.pinAction, icon: 'pin' }]),
            { id: 'members', label: TEXT.family.membersTitle },
            { id: 'notifications', label: menuChat.notificationsEnabled ? TEXT.family.disableNotifications : TEXT.family.enableNotifications },
            { id: 'archive', label: menuChat.isArchived ? 'Вернуть из архива' : 'В архив', icon: 'archive' },
            { id: 'leave', label: TEXT.family.leaveChat, danger: true, confirm: TEXT.family.leaveConfirm },
          ])
    : [];

  async function handleChatMenuSelect(event) {
    const chat = menuChat;
    menuChat = null;
    if (!chat) {
      return;
    }
    const { id } = event.detail;
    if (id === 'pin') {
      await handleChatPin(chat);
    } else if (id === 'rename') {
      await startRename(chat);
    } else if (id === 'archive') {
      await handleArchive(chat.chatId, chat.isArchived);
    } else if (id === 'delete') {
      await handleDelete(chat.chatId);
    } else if (id === 'share' || id === 'members') {
      membersSheetChat = chat;
    } else if (id === 'notifications') {
      await handleNotifications(chat.chatId, !chat.notificationsEnabled);
    } else if (id === 'leave') {
      await handleLeave(chat.chatId);
    }
  }

  async function handleChatPin(chat) {
    actionError = '';
    const pinned = !chat.isPinned;
    try {
      await toggleChatPin(chat.chatId, pinned);
    } catch (error) {
      // 409 — лимит закреплённых; общий classifyChatError его не знает и показал бы
      // английский detail сервера. После лимита освежаем ленту: слот мог занять
      // другое устройство того же пользователя.
      if (error?.status === 409) {
        showFlash(TEXT.chats.pinLimit, 'error', 6000);
        await refreshChatsInBackground();
        return;
      }
      showFlash(classifyChatError(error, pinned ? TEXT.chats.pinFailed : TEXT.chats.unpinFailed), 'error');
    }
  }

  // Этап 28: iOS сбросила разрешение на уведомления — вернуть одним касанием.
  let pushRestoreBusy = false;

  async function handlePushRestore() {
    if (pushRestoreBusy) {
      return;
    }
    pushRestoreBusy = true;
    try {
      const result = await restorePushFromBanner();
      showFlash(pushRestoreResultText(result), result === 'granted' ? 'info' : 'warning', 6000);
    } finally {
      pushRestoreBusy = false;
    }
  }

  async function handleNotifications(chatId, enabled) {
    actionError = '';
    try {
      if (enabled) {
        // В12 (1.27.6): разрешение, push-подписка устройства и флаг чата — одним тапом.
        showFlash(chatNotificationsResultText(await enableChatNotifications(chatId)), 'info', 6000);
      } else {
        await toggleChatNotifications(chatId, false);
      }
    } catch (error) {
      actionError = classifyChatError(error, TEXT.family.notificationsFailed);
    }
  }

  async function handleLeave(chatId) {
    actionError = '';
    try {
      await api.leaveChat(chatId);
      await loadChats($showArchivedStore, { silent: true });
      showFlash('Вы вышли из чата.', 'info');
    } catch (error) {
      actionError = classifyChatError(error, TEXT.family.leaveFailed);
    }
  }

  function handleMembersChanged(event) {
    if (membersSheetChat) {
      const members = event.detail?.members || [];
      membersSheetChat = { ...membersSheetChat, members, isShared: members.length > 1 };
    }
    refreshChatsInBackground();
    if (event.detail?.responseModeChanged) {
      showFlash(TEXT.family.mentionModeHint, 'info', 6000);
    }
  }

  const NEW_CHAT_ACTIONS = [
    { id: 'default', label: 'Обычный чат', icon: 'new-chat' },
    { id: 'scan', label: 'Скан-чат', icon: 'camera' },
    { id: 'list', label: TEXT.list.newChat, icon: 'list' },
  ];

  // Этап 24: шаблоны из библиотеки — подменю «Из библиотеки…».
  $: newChatActions = buildNewChatActions(NEW_CHAT_ACTIONS, $templatesStore, TEXT.templates);

  async function handleNewChatSheetSelect(event) {
    newChatSheetOpen = false;
    const id = event.detail.id;
    const templateId = templateIdFromAction(id);
    if (templateId) {
      await handleNewChat(null, templateId);
      return;
    }
    if (id === 'templates-library') {
      window.location.hash = '/templates';
      return;
    }
    await handleNewChat(id === 'default' ? null : id);
  }

  function formatDate(isoString) {
    return formatChatListDate(isoString, { yesterdayLabel: TEXT.chats.yesterday });
  }

  function getFilterLabel(filterId) {
    if (filterId === 'recent') {
      return 'Недавние';
    }
    if (filterId === 'with-images') {
      return 'С фото';
    }
    if (filterId === 'travel') {
      return 'Путевые';
    }
    if (filterId === 'scan') {
      return 'Скан';
    }
    if (filterId === 'list') {
      return TEXT.list.filter;
    }
    if (filterId === 'archive') {
      return 'Архив';
    }
    if (filterId === 'generating') {
      return 'С активной генерацией';
    }
    return 'Все';
  }

  function highlightText(text, query) {
    const source = text || '';
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      return [{ text: source, match: false }];
    }

    const sourceLower = source.toLocaleLowerCase('ru-RU');
    const queryLower = normalizedQuery.toLocaleLowerCase('ru-RU');
    const matchIndex = sourceLower.indexOf(queryLower);
    if (matchIndex === -1) {
      return [{ text: source, match: false }];
    }

    return [
      { text: source.slice(0, matchIndex), match: false },
      { text: source.slice(matchIndex, matchIndex + normalizedQuery.length), match: true },
      { text: source.slice(matchIndex + normalizedQuery.length), match: false },
    ].filter((part) => part.text);
  }

  function previewText(chat) {
    if (!chat.lastMessagePreview && chat.chatType === 'list') {
      // В чате-списке содержимое — сам список, а не переписка.
      return chat.listSummary?.total
        ? `${TEXT.list.panelTitle} · ${listSummaryLabel(chat)}`
        : TEXT.list.empty;
    }
    return chat.lastMessagePreview || 'Сообщений пока нет';
  }

  function listSummaryLabel(chat) {
    return TEXT.list.summary
      .replace('{done}', String(chat.listSummary?.done ?? 0))
      .replace('{total}', String(chat.listSummary?.total ?? 0));
  }

  function listBadgeLabel(chat) {
    return chat.listSummary?.total ? listSummaryLabel(chat) : TEXT.list.badge;
  }

  function resultRoleLabel(role) {
    if (role === 'user') {
      return 'Вы';
    }
    if (role === 'assistant') {
      return 'Ответ';
    }
    return 'Система';
  }

  function resultMatchLabel(matchType) {
    if (matchType === 'hybrid') {
      return 'Текст и смысл';
    }
    if (matchType === 'semantic') {
      return 'По смыслу';
    }
    return 'Текст';
  }

  function openSearchResult(result) {
    setCurrentChat(result.chatId);
    // Этап 23: результат внутри свёрнутого этапа открывается окном этого этапа.
    const stageParam = result.stageId ? `&stage=${encodeURIComponent(result.stageId)}` : '';
    window.location.hash = `/chat/${result.chatId}?messageId=${encodeURIComponent(result.messageId)}${stageParam}`;
  }

  async function switchArchiveTab(archivedOnly) {
    await loadChats(archivedOnly);
    if (isSearchMode) {
      await runSearch(debouncedSearchQuery);
    }
  }

  // В4 (1.27.3): прокрутка списка — та же, что до перехода в чат. Строки уже на месте
  // (chatsStore общий), поэтому хватает одного tick.
  async function restoreListScroll() {
    const { scrollTop } = readChatListView();
    if (!scrollTop) {
      return;
    }
    await tick();
    if (listContainer) {
      listContainer.scrollTop = scrollTop;
    }
  }

  onMount(() => {
    const lastSearch = getSessionChatSearch();
    if (lastSearch) {
      searchQuery = lastSearch;
      if (lastSearch.trim().length >= 3) {
        debouncedSearchQuery = lastSearch.trim();
        runSearch(debouncedSearchQuery);
      }
    }
    syncCleanup = subscribeSyncEvents((event) => {
      if (event?.type === 'chats:refresh' || event?.type === 'chat:deleted') {
        refreshSearchIndex();
      }
    });
    // Обновление списка при возврате из фона делает централизованный resume в App.svelte
    // (resumeApp → refreshChatsInBackground, тихо → chatsStore → реактив visibleChats).
    // Свой visibilitychange убран, чтобы не было двойного refresh и спиннера над списком.
    // Ошибку загрузки показывает сам список (chatsLoadErrorStore, К4) — здесь её не ловим.
    // Список уже загружен (возврат из чата) — обновляем тихо, без затемнения (В4).
    // Этап 30: первый показ сразу после старта — список только что пришёл в /start.
    if (!takeFreshStartChats()) {
      refreshSearchIndex({ silent: $chatsLoadedStore }).catch(() => {});
    }
    restoreListScroll();
    // Библиотека шаблонов (этап 24) — заранее, чтобы шторка «+» открывалась без запроса;
    // этап 30: раз за сеанс, а не при каждом возврате из чата.
    ensureTemplatesLoaded();
    return () => {
      saveChatListView({ filter: activeFilter, scrollTop: listContainer?.scrollTop ?? 0 });
      syncCleanup?.();
      syncCleanup = null;
      if (searchDebounceTimer) {
        window.clearTimeout(searchDebounceTimer);
      }
    };
  });
</script>

<div class="chat-list-page" use:pageEntrance>
  <header class="header">
    {#if $showArchivedStore}
      <button class="nav-btn" on:click={() => applyListFilter('all')} aria-label="Назад к чатам">←</button>
    {:else}
      <!-- Аватар = вход в общие настройки (кнопка профиля в навбаре). -->
      <a class="profile-link" href="#/app-settings" aria-label="Профиль и настройки">
        {#if $appSettingsStore.avatarDataUrl}
          <img class="profile-avatar" src={$appSettingsStore.avatarDataUrl} alt="Аватар пользователя" />
        {:else}
          <div class="profile-avatar fallback">
            {getDisplayNameFallback($appSettingsStore, $userStore).slice(0, 1).toUpperCase()}
          </div>
        {/if}
      </a>
    {/if}
    <div class="search-field" class:filtered={filterIsActive}>
      <input
        type="search"
        bind:value={searchQuery}
        on:input={scheduleSearch}
        enterkeyhint="search"
        autocapitalize="off"
        placeholder={searchPlaceholder}
        aria-label="Поиск по чатам"
      />
      <button
        type="button"
        class="filter-btn"
        class:active={filterIsActive}
        aria-label={filterIsActive ? `Фильтр: ${getFilterLabel(listFilter)}` : 'Фильтр списка чатов'}
        aria-haspopup="dialog"
        title="Фильтр списка чатов"
        on:click={() => (filterSheetOpen = true)}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <path d="M4 6h16M7 12h10M10 18h4"/>
        </svg>
      </button>
    </div>
    <button
      class="nav-btn compose-btn new-chat-btn"
      use:longpress
      on:longpress={() => (newChatSheetOpen = true)}
      on:click={() => handleNewChat()}
      aria-haspopup="dialog"
      aria-label="Новый чат"
      title="Тап — обычный чат, долгое нажатие — выбор типа"
    >
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M12 20h9"/>
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>
      </svg>
    </button>
  </header>
  <BrainStatusLine />
  <div class="chats-container" bind:this={listContainer}>
    {#if $pushRestoreStore}
      <div class="push-restore" role="status">
        <p>{TEXT.push.bannerText}</p>
        <div class="push-restore-actions">
          <button type="button" class="push-restore-enable" disabled={pushRestoreBusy} on:click={handlePushRestore}>
            {TEXT.push.bannerEnable}
          </button>
          <button type="button" class="push-restore-dismiss" disabled={pushRestoreBusy} on:click={() => dismissPushBanner()}>
            {TEXT.push.bannerDismiss}
          </button>
        </div>
      </div>
    {/if}
    {#if actionError}
      <div class="action-error" role="alert">{actionError}</div>
    {/if}
    {#if $loadingStore && displayedChats.length === 0 && !hasQuery}
      <div class="loading-state">
        <div class="spinner"></div>
      </div>
    {:else}
    {#if hasQuery}
      <div class="search-section-title">Чаты</div>
      {#if displayedChats.length === 0}
        <div class="empty-state compact">
          <p>Среди {$showArchivedStore ? 'архивных' : 'активных'} чатов ничего не нашли</p>
        </div>
      {/if}
    {/if}
    {#if displayedChats.length === 0 && !hasQuery && $chatsLoadErrorStore}
      <div class="empty-state load-error" role="alert">
        <p>{$chatsLoadErrorStore}</p>
        <button class="create-btn" on:click={() => refreshSearchIndex().catch(() => {})}>
          {TEXT.chats.loadRetry}
        </button>
      </div>
    {:else if displayedChats.length === 0 && !hasQuery && !$chatsLoadedStore}
      <div class="loading-state">
        <div class="spinner"></div>
      </div>
    {:else if displayedChats.length === 0 && !hasQuery}
      <div class="empty-state">
        <p>
          {#if activeFilter !== 'all'}
            Нет чатов для фильтра «{searchSummary}»
          {:else}
            {$showArchivedStore ? 'Нет архивных чатов' : 'Нет активных чатов'}
          {/if}
        </p>
        {#if !$showArchivedStore}
          <button class="create-btn" on:click={() => handleNewChat()}>
            Создать первый чат
          </button>
        {/if}
      </div>
    {:else if displayedChats.length > 0}
      {#each chatGroups as group (group.id)}
      {#if group.title}
        <div class="search-section-title" id="chat-group-{group.id}">{group.title}</div>
      {/if}
      <ul
        class="chat-items"
        class:refreshing={$loadingStore}
        aria-labelledby={group.title ? `chat-group-${group.id}` : undefined}
        aria-label={!group.title && group.aria ? group.aria : undefined}
      >
        {#each group.items as chat (chat.chatId)}
          <li class="chat-item">
            {#if editingChatId === chat.chatId}
              <div class="edit-row">
                <input
                  bind:this={editInput}
                  type="text"
                  bind:value={editTitle}
                  on:keydown={handleKeydown}
                  on:blur={saveRename}
                  placeholder="Название чата"
                />
              </div>
            {:else}
              <div class="swipe-action to-left" aria-hidden="true">
                {chat.isArchived ? TEXT.chats.swipeUnarchive : TEXT.chats.swipeArchive}
              </div>
              {#if !chat.isArchived}
                <div class="swipe-action to-right" aria-hidden="true">
                  {chat.isPinned ? TEXT.chats.swipeUnpin : TEXT.chats.swipePin}
                </div>
              {/if}
              <div
                class="chat-row"
                use:rowSwipe={{ left: true, right: !chat.isArchived, leaveOnLeft: true, onCommit: (direction) => handleRowSwipe(chat, direction) }}
              >
              <a
                href="#/chat/{chat.chatId}"
                class="chat-link"
                draggable="false"
                use:longpress
                on:longpress={() => (menuChat = chat)}
                on:click={() => { setEntrance('right'); setCurrentChat(chat.chatId); }}
              >
                <div class="chat-info">
                  <div class="chat-headline">
                    <span class="chat-title">
                      {#each highlightText(chat.title || 'Новый чат', normalizedSearchQuery) as part}
                        <span class:match={part.match}>{part.text}</span>
                      {/each}
                    </span>
                    <span class="chat-meta">
                      <span class="chat-date">{formatDate(chat.lastMessageTs || chat.updatedAt)}</span>
                      {#if chat.isPinned}
                        <span class="state-badge pinned" title={TEXT.chats.pinnedBadge} aria-label={TEXT.chats.pinnedBadge}>📌</span>
                      {/if}
                      {#if hasQuery && chat.isArchived}
                        <span class="archive-badge">Архив</span>
                      {/if}
                      {#if chat.hasImages}
                        <span class="state-badge media">Фото</span>
                      {/if}
                      {#if chat.chatType === 'travel'}
                        <span class="state-badge travel">Путевой</span>
                      {/if}
                      {#if chat.chatType === 'scan'}
                        <span class="state-badge scan">Скан</span>
                      {/if}
                      {#if chat.chatType === 'list'}
                        <span class="state-badge list">{listBadgeLabel(chat)}</span>
                      {/if}
                      {#if chat.chatType === 'dacha' || chat.brain === 'dacha'}
                        <span class="state-badge dacha">{TEXT.dacha.badge}</span>
                      {/if}
                      {#if isGeneratingState($chatActivityStore[chat.chatId]?.state)}
                        <span class="state-badge generating">В работе</span>
                      {/if}
                      {#if chat.notificationsEnabled}
                        <span class="state-badge notify" title={TEXT.family.notificationsBadge} aria-label={TEXT.family.notificationsBadge}>🔔</span>
                      {/if}
                      {#if chat.unreadCount > 0}
                        <span class="unread-badge" aria-label={TEXT.family.unreadAria.replace('{count}', String(chat.unreadCount))}>{chat.unreadCount > 99 ? '99+' : chat.unreadCount}</span>
                      {/if}
                      {#if chat.isShared}
                        <span class="member-stack" title={TEXT.family.sharedBadge}>
                          {#each (chat.members || []).slice(0, 3) as member (member.userId)}
                            {#if $usersStore[member.userId]?.avatarDataUrl}
                              <img class="member-mini" src={$usersStore[member.userId].avatarDataUrl} alt="" />
                            {:else}
                              <span class="member-mini member-mini-fallback">{($usersStore[member.userId]?.displayName || member.userId).slice(0, 1).toUpperCase()}</span>
                            {/if}
                          {/each}
                          {#if (chat.members || []).length > 3}
                            <span class="member-mini member-mini-fallback">+{(chat.members || []).length - 3}</span>
                          {/if}
                        </span>
                      {/if}
                    </span>
                  </div>
                  <span class="chat-preview">
                    <span class="chat-preview-label">{getChatPreviewLabel(chat)}:</span>
                    {#each highlightText(previewText(chat), normalizedSearchQuery) as part}
                      <span class:match={part.match}>{part.text}</span>
                    {/each}
                  </span>
                </div>
              </a>
              <div class="chat-menu">
                <button class="menu-btn" on:click={(e) => openChatMenu(chat, e)} aria-label="Действия чата" aria-haspopup="dialog">
                  ⋮
                </button>
              </div>
              </div>
            {/if}
          </li>
        {/each}
      </ul>
      {/each}
    {/if}
    {#if isSearchMode}
      <div class="search-section-title">Совпадения в сообщениях</div>
      {#if searchLoading && searchResults.length === 0}
        <div class="loading-state compact">
          <div class="spinner"></div>
        </div>
      {:else if searchResults.length === 0}
        <div class="empty-state compact">
          <p>В сообщениях ничего не нашли</p>
        </div>
      {:else}
        <ul class="chat-items search-results">
          {#each searchResults as result (result.messageId)}
            <li class="chat-item search-result">
              <button type="button" class="chat-link search-link" on:click={() => openSearchResult(result)}>
                <div class="chat-info">
                  <div class="chat-headline">
                    <span class="chat-title">{result.chatTitle || 'Новый чат'}</span>
                    <span class="chat-meta">
                      <span class="chat-date">{formatDate(result.ts)}</span>
                      {#if result.isArchived}
                        <span class="archive-badge">Архив</span>
                      {/if}
                      <span class="state-badge search-kind">{resultMatchLabel(result.matchType)}</span>
                    </span>
                  </div>
                  <span class="chat-preview">
                    <span class="chat-preview-label">{resultRoleLabel(result.role)}:</span>
                    {#each highlightText(result.snippet, debouncedSearchQuery) as part}
                      <span class:match={part.match}>{part.text}</span>
                    {/each}
                  </span>
                </div>
              </button>
            </li>
          {/each}
        </ul>
        {#if searchHasMore}
          <div class="search-more-row">
            <button type="button" class="search-more-btn" on:click={loadMoreSearchResults} disabled={searchLoading}>
              {searchLoading ? 'Загрузка...' : 'Показать ещё'}
            </button>
          </div>
        {/if}
      {/if}
    {/if}
    {/if}
  </div>

  <ActionSheet
    open={menuChat !== null}
    title={menuChat?.title || 'Новый чат'}
    actions={chatMenuActions}
    on:select={handleChatMenuSelect}
    on:close={() => (menuChat = null)}
  />

  <ActionSheet
    open={newChatSheetOpen}
    title="Новый чат"
    actions={newChatActions}
    on:select={handleNewChatSheetSelect}
    on:close={() => (newChatSheetOpen = false)}
  />

  <ActionSheet
    open={filterSheetOpen}
    title="Показать"
    actions={filterSheetActions}
    on:select={handleFilterSelect}
    on:close={() => (filterSheetOpen = false)}
  />

  <ChatMembersSheet
    open={membersSheetChat !== null}
    chatId={membersSheetChat?.chatId || null}
    members={membersSheetChat?.members || []}
    isOwner={membersSheetChat ? membersSheetChat.isOwner !== false : false}
    roster={$usersStore}
    myUserId={$userStore}
    on:changed={handleMembersChanged}
    on:close={() => (membersSheetChat = null)}
  />
</div>

<style>
  .chat-list-page {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  /* Навбар — единственная строка над списком: [аватар][поиск][написать]. */
  .header {
    display: grid;
    /* Аватар, поиск и дальше — сколько угодно кнопок по 44px в одну строку.
       Жёсткие колонки роняли лишний элемент на вторую строку. */
    grid-template-columns: 48px minmax(0, 1fr);
    grid-auto-flow: column;
    grid-auto-columns: 44px;
    align-items: center;
    gap: 0.5rem;
    min-height: 56px;
    padding: 0.25rem var(--spacing-md);
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
  }

  .nav-btn {
    width: 44px;
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 12px;
    background: transparent;
    color: var(--accent-color);
    font-size: 1.5rem;
    cursor: pointer;
  }

  .search-field {
    position: relative;
    min-width: 0;
  }

  .search-field input {
    width: 100%;
    height: 38px;
    padding: 0 40px 0 0.75rem;
    border-radius: 10px;
    border: 1px solid var(--border-light);
    background: var(--bg-primary);
    color: var(--text-primary);
    font-size: 1rem;
  }

  .search-field.filtered input {
    border-color: var(--accent-color);
  }

  /* В11 (1.27.2): зона касания добрана до 44px невидимым ::after, поле не меняется. */
  .filter-btn::after {
    content: '';
    position: absolute;
    inset: -6px;
  }

  .filter-btn {
    position: absolute;
    right: 3px;
    top: 50%;
    transform: translateY(-50%);
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
  }

  .filter-btn.active {
    color: var(--accent-color);
    background: color-mix(in srgb, var(--accent-color) 14%, transparent);
  }

  .profile-avatar {
    width: 48px;
    height: 48px;
    border-radius: 16px;
    flex: 0 0 auto;
    object-fit: cover;
    display: block;
  }

  .profile-link {
    /* Аватар 48px сам по себе больше цели касания 44px (HIG). */
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    flex: 0 0 auto;
    border-radius: 18px;
  }

  .profile-avatar.fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--accent-color);
    color: #fff;
    font-weight: 700;
  }

  .chats-container {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .loading-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 1rem;
    color: var(--text-secondary);
  }

  /* Этап 28: плашка «Уведомления на этом устройстве выключены». Кнопки ≥44 px (В11). */
  .push-restore {
    margin: var(--spacing-md);
    padding: 0.75rem 1rem;
    border-radius: 10px;
    background: var(--warning-bg);
    color: var(--warning-text);
    font-size: 0.875rem;
  }

  .push-restore p {
    margin: 0 0 0.5rem;
  }

  .push-restore-actions {
    display: flex;
    gap: 0.5rem;
  }

  .push-restore-actions button {
    min-height: 44px;
    padding: 0 1rem;
    border-radius: 8px;
    font-weight: 500;
  }

  .push-restore-enable {
    background: var(--accent-color);
    color: white;
    border: none;
  }

  .push-restore-dismiss {
    background: transparent;
    color: inherit;
    border: 1px solid currentColor;
  }

  .action-error {
    margin: var(--spacing-md);
    padding: 0.75rem 1rem;
    border-radius: 10px;
    background: var(--error-bg);
    color: var(--error-text);
    font-size: 0.875rem;
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-color);
    border-top-color: var(--accent-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .create-btn {
    padding: 0.75rem 1.5rem;
    background: var(--accent-color);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 500;
  }

  .chat-items.refreshing {
    opacity: 0.6;
    transition: opacity var(--transition-fast);
  }

  .search-section-title {
    padding: 0.85rem var(--spacing-lg) 0.35rem;
    font-size: 0.8125rem;
    color: var(--text-muted);
  }

  .empty-state.compact,
  .loading-state.compact {
    padding: 0.75rem var(--spacing-lg) 1rem;
    height: auto;
  }

  .chat-items {
    list-style: none;
  }

  .chat-item {
    position: relative;
    display: flex;
    align-items: stretch;
    min-width: 0;
    overflow: hidden;
    border-bottom: 1px solid var(--border-light);
  }

  /* В5: строка едет за пальцем поверх подписи действия (actions/row-swipe.js двигает её
     сам, без перерисовки списка). Подпись видна по data-swipe, который ставит действие. */
  .chat-row {
    position: relative;
    z-index: 1;
    flex: 1;
    display: flex;
    align-items: stretch;
    min-width: 0;
    background: var(--bg-primary);
    transition: transform 0.2s ease;
    will-change: transform;
  }

  .swipe-action {
    position: absolute;
    inset: 0;
    display: none;
    align-items: center;
    padding: 0 1.25rem;
    font-size: 0.9375rem;
    font-weight: 600;
    opacity: 0.7;
    transition: opacity 0.12s ease;
  }

  /* data-swipe ставит actions/row-swipe.js, в шаблоне его нет — без :global Svelte
     счёл бы правила неиспользуемыми и вырезал (так и было в 1.27.4: подписи пропали). */
  .chat-item:global([data-swipe='left']) .swipe-action.to-left,
  .chat-item:global([data-swipe='right']) .swipe-action.to-right {
    display: flex;
  }

  .chat-item:global([data-swipe-armed='1']) .swipe-action {
    opacity: 1;
  }

  .swipe-action.to-left {
    justify-content: flex-end;
    background: var(--warning-bg);
    color: var(--warning-text);
  }

  .swipe-action.to-right {
    justify-content: flex-start;
    background: var(--accent-color);
    color: #fff;
  }

  @media (prefers-reduced-motion: reduce) {
    .chat-row {
      transition: none;
    }
  }

  .chat-link {
    flex: 1;
    display: flex;
    min-width: 0;
    /* Удержание строки — меню чата (В5), а не системное меню ссылки iOS. Перетаскивание
       ссылки выключено: мышью оно начинается раньше свайпа и гасит его pointercancel. */
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    -webkit-user-drag: none;
    padding: 1rem var(--spacing-lg);
    color: inherit;
    text-decoration: none;
    transition: background var(--transition-fast);
  }

  .search-link {
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    font: inherit;
    cursor: pointer;
  }

  .chat-link :global(img) {
    -webkit-user-drag: none;
  }

  .chat-link:hover {
    background: var(--bg-tertiary);
  }

  .chat-info {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    min-width: 0;
    flex: 1;
  }

  .chat-headline {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem 0.75rem;
    align-items: flex-start;
  }

  .chat-title {
    display: block;
    flex: 1 1 10rem;
    min-width: 0;
    font-weight: 500;
    line-height: 1.35;
    /* Кламп: длинный заголовок не растягивает строку списка на пол-экрана. */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .chat-preview {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    color: var(--text-secondary);
    font-size: 0.875rem;
    line-height: 1.35;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .chat-preview-label {
    color: var(--text-muted);
    margin-right: 0.35rem;
  }

  .chat-date {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .chat-meta {
    display: flex;
    flex: 0 1 auto;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-left: auto;
    max-width: 100%;
    justify-content: flex-end;
  }

  .unread-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: var(--accent-color);
    color: var(--accent-contrast, #fff);
    font-size: 0.75rem;
    font-weight: 600;
  }

  .member-stack {
    display: inline-flex;
    align-items: center;
  }

  .member-mini {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
    background: var(--bg-tertiary);
    border: 1.5px solid var(--bg-secondary);
    margin-left: -8px;
  }

  .member-mini:first-child {
    margin-left: 0;
  }

  .member-mini-fallback {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--text-secondary);
  }

  .archive-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    font-size: 0.75rem;
    line-height: 1.2;
  }

  .state-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    font-size: 0.75rem;
    line-height: 1.2;
    background: var(--bg-tertiary);
    color: var(--text-secondary);
  }

  .state-badge.media {
    background: color-mix(in srgb, var(--accent-color) 15%, transparent);
    color: var(--accent-color);
  }

  .state-badge.travel {
    background: rgba(14, 165, 233, 0.14);
    color: #0284c7;
  }

  .state-badge.list {
    background: rgba(90, 140, 255, 0.16);
    color: #5a8cff;
  }

  .state-badge.scan {
    background: rgba(139, 92, 246, 0.16);
    color: #7c3aed;
  }

  .state-badge.generating {
    background: rgba(22, 163, 74, 0.14);
    color: #16a34a;
  }

  .state-badge.dacha {
    background: rgba(101, 163, 13, 0.16);
    color: #4d7c0f;
  }

  /* Уведомления (этап 12) и закрепление (этап 11): значки без плашки — это статусы
     строки, а не типы чата, и они не должны спорить за внимание с бейджами типа. */
  .state-badge.notify,
  .state-badge.pinned {
    background: none;
    padding: 0;
    font-size: 0.8rem;
    line-height: 1;
  }

  .state-badge.search-kind {
    background: color-mix(in srgb, var(--accent-color) 12%, transparent);
    color: var(--accent-color);
  }

  .search-more-row {
    display: flex;
    justify-content: center;
    padding: 0.75rem var(--spacing-lg) 1rem;
  }

  .search-more-btn {
    padding: 0.55rem 1rem;
    border-radius: 8px;
    border: 1px solid var(--border-color);
    background: var(--bg-secondary);
    color: var(--text-primary);
  }

  .search-more-btn:disabled {
    opacity: 0.65;
    cursor: default;
  }

  .match {
    background: color-mix(in srgb, var(--accent-color) 18%, transparent);
    color: var(--text-primary);
  }

  .chat-menu {
    position: relative;
    padding-right: var(--spacing-md);
  }

  .menu-btn {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: var(--text-secondary);
    font-size: 1.25rem;
    border-radius: 4px;
  }

  .menu-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }

  .edit-row {
    flex: 1;
    padding: 0.5rem var(--spacing-lg);
  }

  .edit-row input {
    width: 100%;
  }
</style>
